/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { Mail, Pencil, Plus, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreateEmailConfigRequest,
  EmailConfiguration,
  EmailEvent,
  createEmailConfiguration,
  deleteEmailConfiguration,
  fetchEmailConfigurations,
  updateEmailConfiguration,
} from '../../../services/emailConfigService';
import { selectHasAccessControl } from '../../../store/auth/authSelectors';
import { apiError, apiSuccess } from '../../../util/notify';
import { ReadOnlyAccessNotice } from '../../common';

const EMAIL_EVENT_OPTIONS: {
  value: EmailEvent;
  label: string;
  description: string;
}[] = [
  {
    value: 'sales_report',
    label: 'Daily Sales Report',
    description: 'Receive the end-of-day sales summary report',
  },
  {
    value: 'stock_balances_report',
    label: 'Daily Stock Balances Report',
    description:
      'Receive the end-of-day stock on hand report as an additional attachment on the sales report email',
  },
  {
    value: 'accommodation_booking',
    label: 'Accommodation Booking Notifications',
    description:
      'Receive a copy of booking status update emails sent to guests',
  },
  {
    value: 'accommodation_report',
    label: 'End of Day Accommodation Report',
    description:
      'Receive the daily accommodation revenue and occupancy summary report',
  },
  {
    value: 'credit_settlement_email',
    label: 'Credit Bill Notifications',
    description:
      'Receive a copy of credit settlement emails sent to clients when an order is placed on their tab',
  },
  {
    value: 'billing_notifications',
    label: 'Billing Notifications',
    description:
      'Receive subscription payment receipts with the invoice and receipt PDFs attached',
  },
];

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  events: EmailEvent[];
}

const EMPTY_FORM: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  events: [],
};

interface ConfigFormModalProps {
  isOpen: boolean;
  outletId: string;
  editing: EmailConfiguration | null;
  onClose: () => void;
  onSaved: (config: EmailConfiguration) => void;
}

const ConfigFormModal: React.FC<ConfigFormModalProps> = ({
  isOpen,
  outletId,
  editing,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(
        editing
          ? {
              first_name: editing.first_name,
              last_name: editing.last_name,
              email: editing.email,
              events: editing.events,
            }
          : EMPTY_FORM,
      );
      setTimeout(() => firstRef.current?.focus(), 80);
    }
  }, [isOpen, editing]);

  if (!isOpen) return null;

  const toggleEvent = (event: EmailEvent) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.first_name.trim()) return;

    setIsSaving(true);
    try {
      let saved: EmailConfiguration;
      if (editing) {
        saved = await updateEmailConfiguration({
          id: editing.id,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          events: form.events,
        });
      } else {
        const req: CreateEmailConfigRequest = {
          outlet_id: outletId,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          events: form.events,
        };
        saved = await createEmailConfiguration(req);
      }
      apiSuccess(editing ? 'Configuration updated.' : 'Recipient added.');
      onSaved(saved);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing ? 'Edit recipient' : 'Add recipient'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body scrolls; the footer actions below always stay visible. */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="ec-first"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  First name *
                </label>
                <input
                  id="ec-first"
                  ref={firstRef}
                  type="text"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, first_name: e.target.value }))
                  }
                  required
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
              </div>
              <div>
                <label
                  htmlFor="ec-last"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Last name
                </label>
                <input
                  id="ec-last"
                  type="text"
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, last_name: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="ec-email"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Email address *
              </label>
              <input
                id="ec-email"
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((p) => ({ ...p, email: e.target.value }))
                }
                required
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                placeholder="name@example.com"
              />
            </div>

            <div>
              <p className="mb-2 text-xs font-medium text-gray-700">
                Notifications to receive
              </p>
              <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {EMAIL_EVENT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={form.events.includes(opt.value)}
                      onChange={() => toggleEvent(opt.value)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-pos-primary focus:ring-pos-primary/30"
                    />
                    <div>
                      <p className="text-xs font-medium text-gray-800">
                        {opt.label}
                      </p>
                      <p className="text-xs text-gray-400">{opt.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSaving || !form.email.trim() || !form.first_name.trim()
              }
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSaving
                ? 'Saving…'
                : editing
                  ? 'Save changes'
                  : 'Add recipient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const EmailConfigurationsPage: React.FC = () => {
  const canManage = useAppSelector(
    selectHasAccessControl(AccessControlCode.SETTINGS_EMAIL_CONFIG_MANAGE),
  );

  const outletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);

  const [configs, setConfigs] = useState<EmailConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<EmailConfiguration | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!outletId) return;
    setIsLoading(true);
    try {
      const data = await fetchEmailConfigurations(outletId);
      setConfigs(data);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load configurations',
      );
    } finally {
      setIsLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this email recipient?')) return;
    try {
      await deleteEmailConfiguration(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
      apiSuccess('Recipient removed.');
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleSaved = (config: EmailConfiguration) => {
    setConfigs((prev) => {
      const idx = prev.findIndex((c) => c.id === config.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = config;
        return next;
      }
      return [...prev, config];
    });
    setIsModalOpen(false);
    setEditingConfig(null);
  };

  const handleToggleActive = async (config: EmailConfiguration) => {
    try {
      const updated = await updateEmailConfiguration({
        id: config.id,
        is_active: !config.is_active,
      });
      setConfigs((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c)),
      );
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const eventLabel = (ev: EmailEvent) =>
    EMAIL_EVENT_OPTIONS.find((o) => o.value === ev)?.label ?? ev;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {!canManage && (
        <ReadOnlyAccessNotice message="You can view email configurations here, but only authorised roles can add, edit, or remove recipients." />
      )}

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Email Configurations
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {effectiveOutlet
              ? `Managing recipients for ${effectiveOutlet.name}.`
              : 'Select an outlet above to manage its email recipients.'}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => {
              setEditingConfig(null);
              setIsModalOpen(true);
            }}
            disabled={!outletId}
            className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
            Add Recipient
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center text-sm text-gray-400">
          Loading…
        </div>
      ) : configs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 py-16 text-center">
          <Mail size={36} className="text-gray-300" />
          <p className="text-sm font-medium text-gray-500">
            No email recipients configured
          </p>
          <p className="text-xs text-gray-400">
            {outletId
              ? 'Add recipients to start receiving sales reports and booking notifications by email.'
              : 'Select an outlet from the switcher above, then add recipients for it.'}
          </p>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setEditingConfig(null);
                setIsModalOpen(true);
              }}
              disabled={!outletId}
              className="mt-2 flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={15} />
              Add First Recipient
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((config) => (
            <div
              key={config.id}
              className={[
                'rounded-2xl border bg-white p-4',
                config.is_active
                  ? 'border-gray-200'
                  : 'border-gray-100 opacity-60',
              ].join(' ')}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {config.first_name} {config.last_name}
                    </p>
                    {!config.is_active && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{config.email}</p>
                  {config.events.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {config.events.map((ev) => (
                        <span
                          key={ev}
                          className="rounded-full bg-pos-primary/10 px-2.5 py-0.5 text-[10px] font-medium text-pos-primary"
                        >
                          {eventLabel(ev)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs text-gray-400">
                      No notifications selected
                    </p>
                  )}
                </div>

                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(config)}
                      title={config.is_active ? 'Deactivate' : 'Activate'}
                      className={[
                        'rounded-lg px-2 py-1 text-[10px] font-medium border transition-colors',
                        config.is_active
                          ? 'border-gray-200 text-gray-500 hover:bg-gray-100'
                          : 'border-green-200 text-green-600 hover:bg-green-50',
                      ].join(' ')}
                    >
                      {config.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingConfig(config);
                        setIsModalOpen(true);
                      }}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(config.id)}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfigFormModal
        isOpen={isModalOpen}
        outletId={outletId ?? ''}
        editing={editingConfig}
        onClose={() => {
          setIsModalOpen(false);
          setEditingConfig(null);
        }}
        onSaved={handleSaved}
      />
    </div>
  );
};
